export function formatMoney(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// =============================================================================
// Date formatting helpers — ALL times are in Africa/Nairobi (EAT / UTC+3)
// =============================================================================
const SHORT_MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FULL_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const NAIROBI_OFFSET_MINUTES = 3 * 60; // UTC+3

function getOrdinalSuffix(day) {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

// Convert any Date (or date-like input) to a Date in Africa/Nairobi time
export function toNairobiDate(input) {
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;

  // Normalize to UTC, then apply fixed +3h offset for EAT
  const utcMs = d.getTime() + d.getTimezoneOffset() * 60000;
  const nairobiMs = utcMs + NAIROBI_OFFSET_MINUTES * 60000;
  return new Date(nairobiMs);
}

// Get current date/time in Nairobi
export function getNairobiNow() {
  return toNairobiDate(new Date());
}

// Get current month (1-12) in Nairobi time
export function getNairobiMonth() {
  const d = getNairobiNow();
  return d ? d.getMonth() + 1 : new Date().getMonth() + 1;
}

// Get current year in Nairobi time
export function getNairobiYear() {
  const d = getNairobiNow();
  return d ? d.getFullYear() : new Date().getFullYear();
}

// Get month name (full) by 1-based month number (1=January, 12=December)
export function getMonthName(monthNum) {
  const idx = (monthNum || 1) - 1;
  return FULL_MONTHS[idx] || FULL_MONTHS[0];
}

// Get short month name by 0-based index
export function getShortMonthName(monthIndex) {
  return SHORT_MONTHS[monthIndex] || '';
}

function getDateParts(input) {
  const d = toNairobiDate(input);
  if (!d) return null;
  return {
    year: d.getFullYear(),
    monthIndex: d.getMonth(),
    day: d.getDate(),
  };
}

export function formatDateCustom(input, options = {}) {
  const { includeYear = true, monthFirst = true } = options;
  const parts = getDateParts(input);
  if (!parts) return '';
  const { year, monthIndex, day } = parts;
  const month = SHORT_MONTHS[monthIndex] || '';
  const ordinal = getOrdinalSuffix(day);
  if (monthFirst) {
    return includeYear ? `${month} ${day}${ordinal} ${year}` : `${month} ${day}${ordinal}`;
  }
  return includeYear ? `${day}${ordinal} ${month} ${year}` : `${day}${ordinal} ${month}`;
}

export function formatDateLong(input) {
  return formatDateCustom(input, { includeYear: true, monthFirst: true });
}

export function formatDateShort(input) {
  return formatDateCustom(input, { includeYear: false, monthFirst: false });
}

export function formatDateWithTime(input) {
  const d = toNairobiDate(input);
  if (!d) return '';

  const year = d.getFullYear();
  const monthIndex = d.getMonth();
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const month = SHORT_MONTHS[monthIndex] || '';
  const dayStr = String(day).padStart(2, '0');
  const hoursStr = String(hours).padStart(2, '0');
  const minutesStr = String(minutes).padStart(2, '0');
  const secondsStr = String(seconds).padStart(2, '0');

  return `${dayStr}/${month}/${year} ${hoursStr}:${minutesStr}:${secondsStr}`;
}

// Full date-time label in Nairobi time with explicit timezone suffix.
// Example: "May 30th 2026 00:00 EAT"
export function formatDateTimeEAT(input, options = {}) {
  const { includeYear = true, includeSeconds = false } = options;
  const datePart = formatDateCustom(input, { includeYear, monthFirst: true });
  const d = toNairobiDate(input);
  if (!datePart || !d) return '';

  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const timePart = includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
  return `${datePart} ${timePart} EAT`;
}

// YYYY-MM-DD string in Africa/Nairobi time (useful for date inputs and filenames)
export function formatDateInputValue(input) {
  const d = toNairobiDate(input);
  if (!d) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Time-only string in Africa/Nairobi (HH:MM:SS)
export function formatTimeOnly(input) {
  const d = toNairobiDate(input);
  if (!d) return '';
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

// Full datetime for display: "Jan 5th 2025 at 14:30:00"
export function formatFullDateTime(input) {
  const datePart = formatDateLong(input);
  const timePart = formatTimeOnly(input);
  if (!datePart || !timePart) return '';
  return `${datePart} at ${timePart}`;
}

// Format created/updated timestamps: "DD/MM/YYYY HH:MM:SS" in Nairobi
export function formatTimestamp(input) {
  const d = toNairobiDate(input);
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

// Parse a date input value (YYYY-MM-DD) and return start of day in Nairobi
export function parseNairobiDateStart(dateStr) {
  if (!dateStr) return null;
  const d = toNairobiDate(dateStr);
  if (!d) return null;
  // Set to start of day
  d.setHours(0, 0, 0, 0);
  return d;
}

// Parse a date input value (YYYY-MM-DD) and return end of day in Nairobi
export function parseNairobiDateEnd(dateStr) {
  if (!dateStr) return null;
  const d = toNairobiDate(dateStr);
  if (!d) return null;
  // Set to end of day
  d.setHours(23, 59, 59, 999);
  return d;
}
